import { ActionIcon, Badge } from '@mantine/core';
import { useMemo } from 'react';

import { EmployeeLink, MaterialIcons, NoResult, Table, type TableLayout } from '@/common/components';
import { McpClientRegistrationType, McpConnectionRevocationReason, McpConnectionStatus } from '@/common/graphql';
import { DateUtils } from '@/common/utils';

import { McpConnectionStatusBadge } from './McpConnectionStatusBadge';

type EmployeeRef = {
  id: string;
  generalInformation: { preferredFullName: string };
};

export type McpConnectionRow = {
  id: string;
  clientName: string;
  clientHost?: string | null;
  registrationType?: McpClientRegistrationType | null;
  createdAt: string;
  lastUsedAt: string;
  status: McpConnectionStatus;
  revokedAt?: string | null;
  revocationReason?: McpConnectionRevocationReason | null;
  employee?: EmployeeRef;
  revokedBy?: EmployeeRef | null;
};

interface McpConnectionTableProps {
  data?: McpConnectionRow[];
  loading: boolean;
  /** Adds a column naming the employee each connection belongs to (the administrator view). */
  showEmployee?: boolean;
  onRevoke: (connection: McpConnectionRow) => void;
}

const REVOCATION_REASON_LABELS: Record<McpConnectionRevocationReason, string> = {
  [McpConnectionRevocationReason.User]: 'Revoked by the employee',
  [McpConnectionRevocationReason.Admin]: 'Revoked by an administrator',
  [McpConnectionRevocationReason.TokenReuse]: 'Revoked after a credential was reused',
  [McpConnectionRevocationReason.EmployeeIneligible]: 'Revoked because the employee can no longer sign in',
};

const RevocationDetail = ({ connection }: { connection: McpConnectionRow }) => {
  if (connection.status !== McpConnectionStatus.Revoked) return null;

  const reason = connection.revocationReason ? REVOCATION_REASON_LABELS[connection.revocationReason] : 'Revoked';
  const by = connection.revokedBy?.generalInformation.preferredFullName;

  return (
    <div className="mt-1 text-gray-7 text-2xs">
      {reason}
      {by ? ` (${by})` : ''}
      {connection.revokedAt ? `, ${DateUtils.formatDateTime(connection.revokedAt)}` : ''}
    </div>
  );
};

export const McpConnectionTable = ({ data, loading, showEmployee = false, onRevoke }: McpConnectionTableProps) => {
  const layout: TableLayout<McpConnectionRow>[] = useMemo(
    () =>
      [
        ...(showEmployee
          ? [
              {
                headerRender: () => 'Employee',
                accessor: 'employee',
                cellRender: (item: McpConnectionRow) =>
                  item.employee ? (
                    <>
                      <EmployeeLink employeeId={item.employee.id}>
                        {item.employee.generalInformation.preferredFullName}
                      </EmployeeLink>
                      <span className="text-gray-7 text-2xs">&nbsp;#{item.employee.id}</span>
                    </>
                  ) : (
                    ''
                  ),
              },
            ]
          : []),
        {
          headerRender: () => 'Application',
          accessor: 'clientName',
          maxWidth: 'auto',
          cellRender: (item: McpConnectionRow) => (
            <div>
              <span className="font-bold">{item.clientName}</span>
              {item.registrationType === McpClientRegistrationType.Dcr && (
                <Badge className="ml-2" color="yellow" variant="light">
                  Self-registered
                </Badge>
              )}
              {item.clientHost && <div className="text-gray-7 text-2xs">{item.clientHost}</div>}
            </div>
          ),
        },
        {
          headerRender: () => 'Approved',
          accessor: 'createdAt',
          tdClassName: 'tabular-nums',
          cellRender: (item: McpConnectionRow) => DateUtils.formatDateTime(item.createdAt),
        },
        {
          headerRender: () => 'Last used',
          accessor: 'lastUsedAt',
          tdClassName: 'tabular-nums',
          cellRender: (item: McpConnectionRow) => DateUtils.formatDateTime(item.lastUsedAt),
        },
        {
          headerRender: () => 'Status',
          accessor: 'status',
          cellRender: (item: McpConnectionRow) => (
            <>
              <McpConnectionStatusBadge status={item.status} />
              <RevocationDetail connection={item} />
            </>
          ),
        },
        {
          headerRender: () => '',
          accessor: 'id',
          tdClassName: 'text-right',
          thClassName: 'text-right',
          cellRender: (item: McpConnectionRow) =>
            item.status === McpConnectionStatus.Revoked ? (
              <></>
            ) : (
              <ActionIcon aria-label={`Revoke ${item.clientName}`} onClick={() => onRevoke(item)}>
                <MaterialIcons iconName="link_off" className="cursor-pointer" />
              </ActionIcon>
            ),
        },
      ] as TableLayout<McpConnectionRow>[],
    [showEmployee, onRevoke],
  );

  return (
    <Table<McpConnectionRow>
      data={data}
      layout={layout}
      loading={loading}
      notFoundTemplate={<NoResult description="Try changing the filters" title="No connected apps found" />}
      wrapperClassName="overflow-auto min-h-[90px]"
    />
  );
};
